import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { AuthenticationService } from './authentication.service';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class MenuAccessGuard implements CanActivate {
  private apiUrl = environment.api_url;
  private allowedRoutes: string[] = [];
  private lastFetchTime: number = 0;
  private cacheDuration: number = 60000; // 1 minute cache
  private lastMode: string = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private authService: AuthenticationService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    // Si l'utilisateur n'est pas connecté, rediriger vers la page de connexion
    if (!this.checkAuthentication()) {
      this.router.navigate(['/auth/login']);
      return false;
    }

    // Obtenir le chemin actuel
    const currentPath = state.url;
    
    // Déterminer le mode courant
    const mode = this.getNavigationMode();

    // Vérifier si le cache est valide et le mode identique
    const now = Date.now();
    if (this.allowedRoutes.length > 0 && (now - this.lastFetchTime) < this.cacheDuration && this.lastMode === mode) {
      return this.checkAccess(currentPath);
    }

    // Sinon, récupérer les menus depuis l'API (et invalider si le mode a changé)
    return this.fetchMenus(mode).pipe(
      map(menus => this.checkAccess(currentPath)),
      catchError(error => {
        console.error('Erreur lors de la récupération des menus:', error);
        // En cas d'erreur, autoriser l'accès par défaut
        return of(true);
      })
    );
  }

  private checkAuthentication(): boolean {
    // Vérifier si le token existe
    const token = localStorage.getItem('token');
    return !!token;
  }

  private fetchMenus(mode: string): Observable<any> {
    const token = localStorage.getItem('token');
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    this.lastMode = mode;
    return this.http.get<any>(`${this.apiUrl}/api/menu/me?mode=${mode}`, { headers }).pipe(
      map(response => {
        this.lastFetchTime = Date.now();
        this.allowedRoutes = this.extractRoutes(response);
        return response;
      })
    );
  }

  private getNavigationMode(): string {
    // Utiliser l'authService pour respecter le mode préféré quand admin+user
    if (this.authService.hasRole('super')) {
      return 'super';
    }
    if (this.authService.hasRole('trainee') || this.authService.hasRoleTrainee()) {
      return 'trainee';
    }
    if (this.authService.hasRoleExternalSupervisor()) {
      return 'external';
    }
    if (this.authService.hasRole('admin') && this.authService.hasRole('user')) {
      return this.authService.getPreferredNavigation();
    }
    if (this.authService.hasRole('admin')) {
      return 'admin';
    }
    if (this.authService.hasRole('user')) {
      return 'user';
    }
    return 'user';
  }

  private getUserRoles(): string[] {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [];
      
      // Décoder le token JWT pour obtenir les rôles
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const payload = JSON.parse(jsonPayload);
      if (payload.roles && Array.isArray(payload.roles)) {
        return payload.roles.map((r: any) => r.role);
      }
      return [];
    } catch (e) {
      console.error('Erreur lors du décodage du token:', e);
      return [];
    }
  }

  private extractRoutes(menus: any[]): string[] {
    const routes: string[] = [];
    
    const extractRoutesRecursive = (items: any[]) => {
      if (!items || !Array.isArray(items)) return;
      
      items.forEach(item => {
        if (item.route) {
          routes.push(item.route);
        }
        if (item.children && Array.isArray(item.children)) {
          extractRoutesRecursive(item.children);
        }
      });
    };
    
    extractRoutesRecursive(menus);
    return routes;
  }

  private checkAccess(currentPath: string): boolean {
    // Normaliser le chemin actuel (supprimer les paramètres de requête, etc.)
    const normalizedPath = this.normalizePath(currentPath);
    
    // Vérifier si le chemin est dans la liste des routes autorisées
    const hasAccess = this.allowedRoutes.some(route => {
      const normalizedRoute = this.normalizePath(route);
      return normalizedPath === normalizedRoute || normalizedPath.startsWith(normalizedRoute + '/');
    });

    if (!hasAccess) {
      // Afficher un message d'avertissement
      Swal.fire({
        title: 'Accès refusé',
        text: "Vous n'avez pas accès à cette page.",
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      
      // Rediriger vers une page par défaut (par exemple, la première route autorisée ou une page d'accueil)
      if (this.allowedRoutes.length > 0) {
        this.router.navigate([this.allowedRoutes[0]]);
      } else {
        this.router.navigate(['/']);
      }
    }
    
    return hasAccess;
  }

  private normalizePath(path: string): string {
    // Supprimer les paramètres de requête
    const queryIndex = path.indexOf('?');
    if (queryIndex !== -1) {
      path = path.substring(0, queryIndex);
    }
    
    // Supprimer les fragments
    const fragmentIndex = path.indexOf('#');
    if (fragmentIndex !== -1) {
      path = path.substring(0, fragmentIndex);
    }
    
    // Normaliser certains chemins spécifiques comme dans collapsable.component.ts
    if (path.startsWith('/profile/list-conventions')) {
      return '/profile/conventions';
    } else if (path.startsWith('/profile/config/update') || path.startsWith('/profile/config/add')) {
      return '/profile/config/list-diplome';
    } else if (path.startsWith('/profile/meetings/add')) {
      return '/profile/meetings/list';
    }
    
    return path;
  }
} 